import React, { useState, useEffect } from "react";
import { errorMapping, makeRequest } from "../../../assets/functions_helper";
import ListData from "../../../components/listData";
import {
  Box,
  DialogContentText,
  Alert,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import {
  CustomDialog,
  CustomDialogActions,
  CustomDialogContent,
  CustomDialogTitle,
} from "../../../components/customDialog";
import { CloseButton, SendButton } from "../../../components/buttons";
import { motion } from "framer-motion";
import { CustomTextField } from "../../../components/textFields";

// Paleta de colores
const COLORS = {
  PRIMARY: "#2596be",
  PRIMARY_HOVER: "#1a7a9e",
  BACKGROUND: "#ffffff",
  PAPER_BG: "#ffffff",
  TEXT_PRIMARY: "#1E1E1E",
  TEXT_SECONDARY: "#6A6A6A",
  BORDER: "#e0e0e0",
  SHADOW: "rgba(37, 150, 190, 0.1)",
};

// Mapeo de roles (id => nombre)
const ROLE_NAMES = {
  2: "Administrador de Restaurante",
  3: "Mesero Líder",
  4: "Mesero",
};

// Configuración de columnas para la tabla
const columnsConfig = [
  {
    header: "Nombres",
    dataKey: "fullName",
    type: "text",
  },
  {
    header: "Correo",
    dataKey: "email",
    type: "text",
  },
  {
    header: "Rol",
    dataKey: "roleName",
    type: "text",
  },
];

function ListUsersRestaurants({
  idRestaurant,
  role = [2],
  title,
  titleCreate,
  titleContent,
  nameOfRestaurant = "",
  waiters = false,
}) {
  const [usersOfRestaurant, setUsersOfRestaurant] = useState([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const [openCreate, setOpenCreate] = useState(false);
  const [updateTrigger, setUpdateTrigger] = useState(0);

  // Estado para creación
  const [newUser, setNewUser] = useState({
    name: "",
    last_name: "",
    email: "",
    password: "",
    role_id: role[0] || 2,
  });

  // Estado para edición
  const [openEdit, setOpenEdit] = useState(false);
  const [editingUser, setEditingUser] = useState({
    id: null,
    name: "",
    last_name: "",
    email: "",
    role_id: 2,
    is_active: true, // lo traemos del usuario original
  });

  // Snackbar unificado
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  const navigate = useNavigate();

  // Cargar usuarios del restaurante
  useEffect(() => {
    let urlBase;
    const rolesQueryString = role.map((id) => `role_ids=${id}`).join("&");

    if (localStorage.getItem("rol") === "2") {
      urlBase = `restaurant_admin/users/${idRestaurant}?${rolesQueryString}`;
    } else {
      urlBase = `restaurants/${idRestaurant}/users?${rolesQueryString}`;
    }

    makeRequest(
      "GET",
      urlBase,
      {},
      "application/json",
      localStorage.getItem("token"),
      localStorage.getItem("tokenType"),
      null,
      null,
      () => navigate("/login")
    )
      .then((response) => {
        let usersData = [];
        if (localStorage.getItem("rol") === "2") {
          usersData = response;
        } else {
          usersData = response.users;
        }

        const updatedUsers = usersData.map((user) => {
          const userRole = user.roles && user.roles[0];
          const roleId = userRole?.id || 0;
          const roleName = ROLE_NAMES[roleId] || "Desconocido";

          return {
            ...user,
            id: user.id,
            fullName: `${user.name} ${user.last_name}`,
            email: user.email,
            roleName: roleName,
            roleId: roleId,
            is_active: user.is_active, // guardamos para edición
          };
        });

        setUsersOfRestaurant(updatedUsers);
        setIsLoaded(true);
      })
      .catch((error) => {
        console.error(error);
        setSnackbar({
          open: true,
          message: "Error al cargar los usuarios",
          severity: "error",
        });
      });
  }, [idRestaurant, updateTrigger, navigate, role]);

  // Crear usuario asignado al restaurante
  const handleCreateUser = () => {
    if (!newUser.name || !newUser.last_name || !newUser.email || !newUser.password) {
      setSnackbar({
        open: true,
        message: "Todos los campos son obligatorios",
        severity: "warning",
      });
      return;
    }

    const url = `users/crud/create_and_assign_user/?restaurant_id=${idRestaurant}&role_id=${newUser.role_id}`;

    makeRequest(
      "POST",
      url,
      {
        name: newUser.name,
        email: newUser.email,
        last_name: newUser.last_name,
        password: newUser.password,
      },
      "application/json",
      localStorage.getItem("token"),
      localStorage.getItem("tokenType"),
      null,
      null,
      () => navigate("/login")
    )
      .then(() => {
        setSnackbar({
          open: true,
          message: "Usuario creado correctamente",
          severity: "success",
        });
        setOpenCreate(false);
        setNewUser({ name: "", last_name: "", email: "", password: "", role_id: role[0] || 2 });
        setUpdateTrigger((prev) => prev + 1);
      })
      .catch((error) => {
        console.log(error);
        if (error && error.error_code && errorMapping[error.error_code]) {
          setSnackbar({ open: true, message: errorMapping[error.error_code], severity: "error" });
        } else if (typeof error === 'string' && error.length > 0) {
          for (const code in errorMapping) {
            if (error.includes(code)) {
              setSnackbar({ open: true, message: errorMapping[code], severity: "error" });
              return;
            }
          }
          setSnackbar({ open: true, message: "Error al crear usuario", severity: "error" });
        } else if (Array.isArray(error) && error.length > 0 && error[0].msg) {
          setSnackbar({ open: true, message: error[0].msg, severity: "error" });
        } else {
          setSnackbar({ open: true, message: "Error al crear usuario", severity: "error" });
        }
      });
  };

  // Abrir modal de edición con datos precargados
  const handleOpenEdit = (item) => {
    setEditingUser({
      id: item.id,
      name: item.name,
      last_name: item.last_name,
      email: item.email,
      role_id: item.roleId,
      is_active: item.is_active,
    });
    setOpenEdit(true);
  };

  // Enviar edición
  const handleEditUser = () => {
    if (!editingUser.name || !editingUser.last_name || !editingUser.email) {
      setSnackbar({
        open: true,
        message: "Nombre, apellido y correo son obligatorios",
        severity: "warning",
      });
      return;
    }

    const url = `restaurant_admin/update_user/${editingUser.id}?role_id=${editingUser.role_id}`;

    const payload = {
      name: editingUser.name,
      email: editingUser.email,
      last_name: editingUser.last_name,
      is_active: editingUser.is_active,
    };

    makeRequest(
      "PUT",
      url,
      payload,
      "application/json",
      localStorage.getItem("token"),
      localStorage.getItem("tokenType"),
      null,
      null,
      () => navigate("/login")
    )
      .then(() => {
        setSnackbar({
          open: true,
          message: "Usuario actualizado correctamente",
          severity: "success",
        });
        setOpenEdit(false);
        setUpdateTrigger((prev) => prev + 1);
      })
      .catch((error) => {
        console.error(error);
        if (error && error.error_code && errorMapping[error.error_code]) {
          setSnackbar({ open: true, message: errorMapping[error.error_code], severity: "error" });
        } else if (typeof error === 'string' && error.length > 0) {
          for (const code in errorMapping) {
            if (error.includes(code)) {
              setSnackbar({ open: true, message: errorMapping[code], severity: "error" });
              return;
            }
          }
          setSnackbar({ open: true, message: "Error al actualizar usuario", severity: "error" });
        } else if (Array.isArray(error) && error.length > 0 && error[0].msg) {
          setSnackbar({ open: true, message: error[0].msg, severity: "error" });
        } else {
          setSnackbar({ open: true, message: "Error al actualizar usuario", severity: "error" });
        }
      });
  };

  if (!isLoaded) {
    return (
      <Box sx={{ height: "100vh", bgcolor: COLORS.BACKGROUND }} />
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      style={{
        backgroundColor: COLORS.BACKGROUND,
        minHeight: "100%",
        overflowY: "auto",
        scrollbarWidth: "none",
        msOverflowStyle: "none",
        "&::-webkit-scrollbar": { display: "none" },
      }}
    >
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <ListData
          title={title}
          data={usersOfRestaurant}
          setData={setUsersOfRestaurant}
          edit={true}
          editMessage="Editar Usuario"
          editFunction={(id, title, public_id, item) => {
            handleOpenEdit(item);
          }}
          page={null}
          setPage={null}
          changePage={() => {}}
          pages={null}
          total={null}
          currentPage={null}
          create={true}
          createMessage={titleCreate}
          createFunction={() => setOpenCreate(true)}
          pagination={false}
          dynamicColumns={columnsConfig}
          nameColumn1=""
          nameColumn2=""
          nameColumn3="Acciones"
        />

        {/* Modal Crear Usuario */}
        <CustomDialog open={openCreate} onClose={() => setOpenCreate(false)}>
          <CustomDialogTitle>{titleCreate}</CustomDialogTitle>
          <CustomDialogContent>
            <DialogContentText sx={{ textAlign: "center", mb: 2 }}>
              {titleContent}
            </DialogContentText>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
              <CustomTextField
                label="Nombre"
                value={newUser.name}
                onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                fullWidth
                size="small"
              />
              <CustomTextField
                label="Apellido"
                value={newUser.last_name}
                onChange={(e) => setNewUser({ ...newUser, last_name: e.target.value })}
                fullWidth
                size="small"
              />
              <CustomTextField
                label="Correo"
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                fullWidth
                size="small"
              />
              <CustomTextField
                label="Contraseña"
                type="password"
                value={newUser.password}
                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                fullWidth
                size="small"
              />
              <FormControl fullWidth size="small">
                <InputLabel>Rol</InputLabel>
                <Select
                  value={newUser.role_id}
                  label="Rol"
                  onChange={(e) => setNewUser({ ...newUser, role_id: e.target.value })}
                >
                  {role.map((r) => (
                    <MenuItem key={r} value={r}>
                      {ROLE_NAMES[r]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </CustomDialogContent>
          <CustomDialogActions>
            <CloseButton onClick={() => setOpenCreate(false)}>Cerrar</CloseButton>
            <SendButton onClick={handleCreateUser}>Enviar</SendButton>
          </CustomDialogActions>
        </CustomDialog>

        {/* Modal Editar Usuario */}
        <CustomDialog open={openEdit} onClose={() => setOpenEdit(false)} maxWidth="sm" fullWidth>
          <CustomDialogTitle>Editar Usuario</CustomDialogTitle>
          <CustomDialogContent dividers>
            <DialogContentText sx={{ textAlign: "center", mb: 2 }}>
              Modifica los campos que desees
            </DialogContentText>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
              <CustomTextField
                label="Nombre"
                value={editingUser.name}
                onChange={(e) => setEditingUser({ ...editingUser, name: e.target.value })}
                fullWidth
                size="small"
              />
              <CustomTextField
                label="Apellido"
                value={editingUser.last_name}
                onChange={(e) => setEditingUser({ ...editingUser, last_name: e.target.value })}
                fullWidth
                size="small"
              />
              <CustomTextField
                label="Correo"
                type="email"
                value={editingUser.email}
                onChange={(e) => setEditingUser({ ...editingUser, email: e.target.value })}
                fullWidth
                size="small"
              />
              <FormControl fullWidth size="small">
                <InputLabel>Rol</InputLabel>
                <Select
                  value={editingUser.role_id}
                  label="Rol"
                  onChange={(e) => setEditingUser({ ...editingUser, role_id: e.target.value })}
                >
                  {[2, 3, 4].map((r) => (
                    <MenuItem key={r} value={r}>
                      {ROLE_NAMES[r]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </CustomDialogContent>
          <CustomDialogActions>
            <CloseButton onClick={() => setOpenEdit(false)}>Cancelar</CloseButton>
            <SendButton onClick={handleEditUser}>Guardar Cambios</SendButton>
          </CustomDialogActions>
        </CustomDialog>

        {/* Snackbar unificado */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert
            severity={snackbar.severity}
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            variant="filled"
            sx={{
              fontWeight: 500,
              boxShadow: `0 4px 12px ${COLORS.SHADOW}`,
              backgroundColor: snackbar.severity === "success" ? COLORS.PRIMARY : undefined,
            }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </motion.div>
  );
}

export default ListUsersRestaurants;